import React, { useEffect, useMemo } from 'react'
import { ThemeProvider } from 'styled-components'
import { PrototypeProvider } from '@sketch-hq/sketch-prototype-player'
import { Redirect } from 'react-router'
import { routes, IS_EMBEDDED } from '@sketch/constants'
import DocumentSidebarLayout from 'containers/DocumentSidebarLayout'
import { useVersioning } from 'modules/versioning'
import {
  useForTablet,
  IsEmbedded,
  NotEmbedded,
  ErrorMessage,
} from '@sketch/components'
import ShareWorkspaceSidebar from '../../shares/components/ShareWorkspaceSidebar'
import {
  PrototypeHeaderEmpty,
  PrototypeHeader,
  PrototypeMobileFooter,
} from './components/PrototypeHeader'
import { getRenderStatus } from 'utils/renderStatus'
import { useGetPrototypeFileForDocumentQuery } from '@sketch/gql-types'
import { useToast } from '@sketch/toasts'
import { PrototypeViewErrorCode } from './types'
import {
  usePrototypeStatusSubscription,
  usePrototypeManifest,
  usePrototypeImagesPreload,
  PrototypeViewContextProvider,
  usePrototypeConfig,
  useInitialPrototypeArtboardId,
  usePrototypeLoadDurationMetric,
  usePrototypeImagePreloadDurationMetric,
} from './hooks'
import { PrototypeLoadingPage } from 'components/LoadingPage/PrototypeLoadingPage'
import { ReactComponent as DocumentProcessing } from '@sketch/icons/document-processing-64-svg'
import { PrototypePlayer } from './components/PrototypePlayer'
import config from '@sketch/env-config'
import { EmbeddedErrorPage } from 'modules/shares/components/EmbeddedErrorPage'
import { darkTheme } from '@sketch/global-styles'
import PageLayout from 'layouts/PageLayout'
import { getErrorMessage } from './utils/errorMessages'

/**
 * Small wrapper necessary so that can PrototypeView access the full screen
 * context needed inside usePrototypeConfig.
 */
export function PrototypeView() {
  return (
    <ThemeProvider theme={darkTheme}>
      <PrototypeViewContextProvider>
        <PrototypeViewInner />
      </PrototypeViewContextProvider>
    </ThemeProvider>
  )
}

/**
 * Before rendering the player, this view has to coordinate a fairly complex
 * serial sequence of actions:
 *
 * 1. Wait for the versioning context to load
 * 2. Query GraphQL for the `Document.prototypeFile` data
 * 3. Use the `prototypeFile` subscription to wait for the prototype to be
 *    rendered on demand, if necessary
 * 4. Load the `manifest.json` file
 * 5. Preload the prototype's images, displaying load progress
 * 6. Finally show the player
 */
function PrototypeViewInner() {
  const versioning = useVersioning()
  const isTabletAndBigger = useForTablet()
  const initialArtboardId = useInitialPrototypeArtboardId()
  const documentRenderStatus = !versioning.loading
    ? getRenderStatus(versioning.currentVersion.document?.renderStatus ?? null)
    : null

  const fileQuery = useGetPrototypeFileForDocumentQuery({
    variables: {
      identifier: versioning.share.identifier,
      versionShortId: versioning.versionShortId,
    },
    skip: documentRenderStatus !== 'ready',
  })

  const prototypeFile = fileQuery?.data?.share?.version?.document?.prototypeFile
  const documentIdentifier =
    fileQuery?.data?.share?.version?.document?.identifier

  const prototypesLink = routes.SHARE_PROTOTYPES.create({
    shareID: versioning.share.identifier,
  })

  usePrototypeStatusSubscription({
    documentRenderStatus,
    documentIdentifier,
    prototypeFile,
  })

  const manifestResult = usePrototypeManifest({
    downloadUrl: prototypeFile?.downloadUrl,
  })

  // Function that transforms an image asset `path` from the manifest.json into
  // the full url for the image in the Cloud infrastructure. We define this on this
  // level since it's used by both the player and the images preloader
  const resolveImageSrc = useMemo(() => {
    if (!prototypeFile) return null
    const { taskId, imageToken } = prototypeFile
    return (path: string) => {
      return `${config.prototype_player_image_domain}/prototype/t/${taskId}/i/${path}?token=${imageToken}`
    }
  }, [prototypeFile])

  const prototypeConfig = usePrototypeConfig({ resolveImageSrc })

  const imagesPreload = usePrototypeImagesPreload({
    manifest:
      (manifestResult.status === 'success' && manifestResult.manifest) || null,
    resolveImageSrc,
    initialArtboardId,
  })

  usePrototypeImagePreloadDurationMetric({
    imagePreloadStatus: imagesPreload.imagePreloadStatus,
    imageCount: imagesPreload.imagesCount,
  })

  usePrototypeLoadDurationMetric({
    loaded: imagesPreload.imagePreloadStatus === 'completed',
    prototypeFile,
  })

  if (versioning.loading || fileQuery.loading) {
    return <LoadingState />
  }

  if (fileQuery.error || !prototypeFile) {
    return <ErrorState code="query_error" prototypesLink={prototypesLink} />
  }

  if (documentRenderStatus !== 'ready' || prototypeFile.status === 'PENDING') {
    return <ProcessingState />
  }

  if (manifestResult.status === 'loading') return <LoadingState />

  if (manifestResult.status === 'error') {
    return (
      <ErrorState code={manifestResult.code} prototypesLink={prototypesLink} />
    )
  }

  if (imagesPreload.imagePreloadStatus === 'error') {
    return <ErrorState code="query_error" prototypesLink={prototypesLink} />
  }

  if (imagesPreload.imagePreloadStatus !== 'completed')
    return <LoadingState progress={imagesPreload.imagePreloadProgress} />

  return (
    <PrototypeProvider
      config={prototypeConfig}
      manifest={manifestResult.manifest}
    >
      <DocumentSidebarLayout
        darkBackground
        sidebarLeft={
          <ShareWorkspaceSidebar
            workspaceIdentifier={versioning.share.workspace.identifier}
          />
        }
        header={headerProps => (
          <PrototypeHeader
            setSidebarLeftOpen={headerProps.setSidebarLeftOpen}
            share={versioning.share}
          />
        )}
        footer={!isTabletAndBigger && <PrototypeMobileFooter />}
      >
        <PrototypePlayer />
      </DocumentSidebarLayout>
    </PrototypeProvider>
  )
}

function LoadingState({ progress }: { progress?: number }) {
  return (
    <PageLayout darkBackground header={<PrototypeHeaderEmpty />}>
      <PrototypeLoadingPage progress={progress} />
    </PageLayout>
  )
}

function ProcessingState() {
  return (
    <PageLayout darkBackground header={<PrototypeHeaderEmpty />}>
      <ErrorMessage
        dark
        title="Rendering Prototype…"
        description="The Prototype is still rendering. When it’s ready, it will appear here."
        icon={<DocumentProcessing />}
      />
    </PageLayout>
  )
}

const ErrorState = ({
  prototypesLink,
  code,
}: {
  prototypesLink: string
  code: PrototypeViewErrorCode
}) => {
  const { showToast } = useToast()
  const errorMessage = getErrorMessage(code)

  useEffect(() => {
    if (!IS_EMBEDDED) showToast(errorMessage, 'negative')
  }, [errorMessage, showToast])

  return (
    <>
      <NotEmbedded>
        <Redirect to={prototypesLink} />
      </NotEmbedded>
      <IsEmbedded>
        <EmbeddedErrorPage for="artboard" />
      </IsEmbedded>
    </>
  )
}
